// =============================================================================
// Risk score composer — turns an Anomaly[] into a RiskScore { total, band,
// weekOverWeek, signals }. Pure function; no I/O.
//
// Bands are absolute, not normalized to subject activity volume. The plan
// (§G.4) calls for an absolute score plus a "sample size: N events" hint
// rendered alongside, so an analyst can calibrate without us hiding the
// underlying activity level.
// =============================================================================

const BANDS = [
  { max: 30,  band: "low" },
  { max: 60,  band: "medium" },
  { max: 85,  band: "high" },
  { max: 1e9, band: "critical" },
];

const _bandFor = (total) => BANDS.find((b) => total <= b.max).band;

// Compose a RiskScore from a current-period anomaly list. `previous` (also an
// Anomaly[]) is optional; when provided, the week-over-week delta is computed
// against the same composition rule. Without it we synth a 0 baseline so the
// delta strip still renders without conditional UI logic.
export function composeRiskScore(anomalies = [], previous = null) {
  const sumWeights = (xs) => xs.reduce((a, x) => a + (x.weight || 0), 0);
  const total = Math.min(100, Math.max(0, Math.round(sumWeights(anomalies))));
  const prevTotal = previous ? Math.min(100, Math.round(sumWeights(previous))) : 0;
  const deltaPct = prevTotal === 0
    ? (total > 0 ? 100 : 0)
    : Math.round(((total - prevTotal) / prevTotal) * 100);

  // Signals are surfaced 1-per-anomaly so the badge row reads like a per-item
  // breakdown rather than a single bucket. Truncated to 8 to keep the row
  // scannable; the AnomaliesCard below the score is the comprehensive list.
  const signals = anomalies.slice(0, 8).map((a) => ({
    kind: a.kind,
    weight: Math.round(a.weight),
    label: a.title,
  }));

  return {
    total,
    band: _bandFor(total),
    weekOverWeek: { previous: prevTotal, deltaPct },
    signals,
  };
}

// Convenience — colour token for a band, used by the score card chip.
export const bandTone = (band) => ({
  low:      "success",
  medium:   "warning",
  high:     "danger",
  critical: "danger",
}[band] || "neutral");
